import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Typography from "@mui/material/Typography"
import * as fileDownload from "js-file-download"
import { useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { useIsMobile } from "../../../common/utils"
import DialogHeader from "../components/DialogHeader"
import { useDialogContainer } from "../utils"

export default function UploadDncDialog({
  open,
  onClose,
  onSuccess,
  uploadUrl = "/crm/api/uploaded-leads/upload-dnc/",
}) {
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [errorText, setErrorText] = useState(null)

  const isMobile = useIsMobile()
  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()

  function resetForm() {
    setErrorText(null)
    setSelectedFile(null)
  }

  function handleSoftClose() {
    if (!isUploading) {
      resetForm()
      onClose()
    }
  }

  function handleFileChanged(e) {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0])
      setErrorText(null)
    } else {
      setSelectedFile(null)
    }
    e.target.value = ""
  }

  function handleDownloadTemplateClicked() {
    const templateContent =
      "Name,Phone\nJane Smith,(555) 123-4567\nDon Stocker,(555) 987-6543\n"
    fileDownload(templateContent, "dnc-list-template.csv", "text/csv")
  }

  function handleUploadButtonClicked() {
    if (!selectedFile) return

    setIsUploading(true)
    setErrorText(null)

    const formData = new FormData()
    formData.append("file", selectedFile)

    axios
      .post(uploadUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        setIsUploading(false)
        resetForm()
        onSuccess(data)
      })
      .catch((err) => {
        let errorMsg = "An unexpected error occurred while uploading DNC list."
        const responseData = err.response?.data
        if (responseData) {
          if (typeof responseData === "string") {
            errorMsg = responseData
          } else if (responseData.detail) {
            errorMsg = responseData.detail
          } else if (responseData.file) {
            errorMsg = Array.isArray(responseData.file)
              ? responseData.file[0]
              : responseData.file
          } else if (responseData.error) {
            errorMsg = responseData.error
          }
        }
        setErrorText(`Error: ${errorMsg}`)
      })
      .finally(() => setIsUploading(false))
  }

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      fullScreen={isMobile}
    >
      <DialogHeader title="Upload DNC List" onClose={handleSoftClose} />
      <DialogContent>
        <Column sx={{ rowGap: 4, mt: 2 }}>
          <Alert severity="error">
            <strong>
              DNC records show in Red to highlight and tag as DNC.
            </strong>
            <br />
            DNC records are not eligible for the dialer, SMS, and email.
          </Alert>

          <Column sx={{ rowGap: 1 }}>
            <Typography variant="h6">Upload DNC List File</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload a CSV of the specific people who should not be contacted.
              Only those listed rows are marked DNC. Other leads at the same
              company stay callable. Use <strong>Name</strong> and/or{" "}
              <strong>Phone</strong>. Office Phone and Company are not used to
              mark DNC.
            </Typography>
            <Button
              variant="outlined"
              component="label"
              color="error"
              startIcon={<CloudUploadOutlinedIcon />}
              sx={{ py: 2, borderStyle: "dashed", borderWidth: 2 }}
            >
              {selectedFile ? selectedFile.name : "Choose DNC CSV File"}
              <input
                type="file"
                hidden
                accept=".csv, text/csv"
                onChange={handleFileChanged}
              />
            </Button>
          </Column>

          <Row sx={{ justifyContent: "center", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Row>

          <Button
            variant="text"
            startIcon={<InsertDriveFileOutlinedIcon />}
            onClick={handleDownloadTemplateClicked}
          >
            Download DNC CSV template
          </Button>

          {errorText ? (
            <Alert severity="error" onClose={() => setErrorText(null)}>
              {errorText}
            </Alert>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSoftClose} disabled={isUploading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          loading={isUploading}
          disabled={!selectedFile}
          onClick={handleUploadButtonClicked}
        >
          Upload DNC
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}
